<template lang="pug">
.microservice-button-container
    el-button(
        :round="appearance === 'round'"
        :circle="appearance === 'circle'"
        :class="buttonClass"
        :type="type"
        :icon="icon"
        :size="size"
        :loading="loading || isGettingProcessed"
        @click="run"
    )
        template(v-if="appearance !== 'circle'") {{ label }}
    el-drawer.iframe-drawer(
        :visible.sync="drawer.visibility"
        :wrapper-closable="false"
        :modal="true"
        size="90%"
        @closed="resetIframeDrawer"
    )
        iframe(:src="drawer.url" height="100%" frameBorder="0")
</template>

<i18n>
{
    "de": {
        "No URL provided": "Keine URL angegeben"
    }
}
</i18n>

<script>

import _ from 'lodash'
import { useSetupStore, useMicroservicesStore } from '@/stores'

export default {
    name: 'MicroserviceButton',
    props: {
        data: {
            type: Object,
        },
        config: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        appearance: {
            type: String,
            default: 'round'
        },
        size: {
            type: String
        },
        buttonClass: {
            type: String | Array | Object
        }
    },
    data: () => ({
        drawer: {
            visibility: false,
            error: false,
            url: undefined,
        },
    }),
    computed: {
        icon() {
            const icon = _.get(this.config, 'button.icon')
            if (! icon) return false

            return `el-icon-${icon}`
        },
        label() {
            return _.get(this.config, 'button.label')
        },
        type() {
            return _.get(this.config, 'button.type')
        },
        key() {
            return _.get(this.config, 'key')
        },
        logs() {
            return _.get(this.config, 'logs') || []
        },
        errorLog() {
            return this.logs.find(el => el.on === 'error')
        },
        successLog() {
            return this.logs.find(el => el.on === 'success')
        },
        callbacks() {
            return _.get(this.config, 'callbacks') || []
        },
        errorCallback() {
            return this.callbacks.find(el => el.on === 'error')
        },
        successCallback() {
            return this.callbacks.find(el => el.on === 'success')
        },
        isGettingProcessed() {
            return this.microservicesStore.processing[this.key]
        },
        setupStore() {
            return useSetupStore()
        },
        microservicesStore() {
            return useMicroservicesStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        settingValues() {
            return this.setupStore.settingValues
        }
    },
    methods: {
        run() {
            const settings = this.settingValues;

            let { key, type, api, url, token } = this.config

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            switch (type ? type.toLowerCase() : undefined) {
                case 'iframe':
                    this.openIframeDrawer(url)
                    break;

                case 'link':
                    this.openLink(url)
                    break;

                default:
                    let data = _.get(this, this.config.data)
                    data.cms = { id: parseInt(this.activeCms.id) }

                    this.microservicesStore.process({ key, api, url, token, data })
                        .then(response => {
                            // check if a log entry needs to be created
                            if (this.successLog) {

                                const obj = { response, data }
                                let payload = _.pick(this.successLog, ['key', 'status', 'heading', 'body', 'loggable_type', 'loggable_id'])

                                if (this.successLog.data) payload.data = JSON.stringify(_.get(obj, this.successLog.data))
                                if (this.successLog.loggable_id) payload.loggable_id = parseInt(_.get(obj, this.successLog.loggable_id))

                                // create a log entry
                                this.microservicesStore.addStatusLog(payload)
                                    .finally(() => this.reportSuccess(response))
                            } else {
                                // no need to create a log entry
                                // this.reportSuccess(result)
                            }
                        })
                        .catch(error => {
                            // check if a log entry needs to be created
                            if (this.errorLog) {

                                const obj = { error, data }
                                let payload = _.pick(this.errorLog, ['key', 'status', 'heading', 'body', 'loggable_type', 'loggable_id'])

                                if (this.errorLog.data) payload.data = JSON.stringify(_.get(obj, this.errorLog.data))
                                if (this.errorLog.loggable_id) payload.loggable_id = parseInt(_.get(obj, this.errorLog.loggable_id))

                                // create a log entry
                                this.microservicesStore.addStatusLog(payload)
                                    .finally(() => this.reportError(error))
                            } else {
                                // no need to create a log entry
                                this.reportError(error)
                            }
                        })
                    break;
            }
        },
        openLink(url) {
            if (url) {
                window.open(url, '_blank').focus();
            } else {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('No URL provided'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                this.$emit('error', 'URL missing')
            }
        },
        openIframeDrawer(url) {
            if (url) {
                this.drawer.visibility = true;
                this.drawer.url = url;
                this.$emit('open-iframe-drawer', url)
            } else {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('No URL provided'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                this.$emit('error', 'URL missing')
            }
        },
        closeIframeDrawer() {
            this.drawer.visibility = false;
            this.$emit('close-iframe-drawer');
        },
        resetIframeDrawer() {
            this.drawer = {
                visibility: false,
                error: false,
                url: undefined,
            };
        },
        reportSuccess(data) {
            if (! this.successCallback) this.microservicesStore.updateProcessing({ key: this.key, status: false });

            const title = _.get(this.successLog, 'heading')
            if (title) {
                const message = _.get(this.successLog, 'body')
                this.$notify.success({
                    title,
                    message,
                    iconClass: 'fas el-icon-fa-check',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            }

            let callback
            if (this.successCallback && this.successCallback.method) callback = this.successCallback.method

            // fire success event
            this.$emit('success', { key: this.key, data, callback })
        },
        reportError(error) {
            if (! this.errorCallback) this.microservicesStore.updateProcessing({ key: this.key, status: false });

            const title = _.get(error, 'response.data.message')
            if (title) {
                let message
                const errors = _.get(error, 'response.data.errors')
                if (errors) {
                    message = errors[Object.keys(errors)[0]]
                    if (_.isArray(message)) message = message[0]
                }
                this.$notify.error({
                    title,
                    message,
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            }

            let callback
            if (this.errorCallback && this.errorCallback.method) callback = this.errorCallback.method

            // fire error event
            this.$emit('error', { key: this.key, error, callback })
        }
    },
}
</script>

<style lang="sass" scoped>
.microservice-button-container
    display: inline-block
</style>
